import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Card, Table } from 'react-bootstrap';
import axios from 'axios';
import { Client, ClientToEquipmentComplementaryItem, ClientToConsumableComplementaryItem, FullEquipment, Category, Subcategory, Consumable } from '../interfaces';
import MessageModal from './MessageModal';
import _ from 'lodash';
// אייקונים
import { 
  faSave, 
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildApiUrl } from '../config/api';

interface ClientFormProps {
  clientToEdit?: Client | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// פונקציית עזר לקיבוץ ציוד מלאי לפי קטגוריה ותת-קטגוריה
const groupEquipment = (equipment: FullEquipment[]) => {
    const grouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    equipment.forEach(item => {
        const category = item.category_name || 'Uncategorized';
        const subcategory = item.subcategory_name || 'Uncategorized';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });
    return grouped;
};

const ClientForm: React.FC<ClientFormProps> = ({ clientToEdit, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<Client>({
    client_name: '',
    contact_person: null,
    phone: null,
    email: null,
    address: null,
    notes: null,
    recommended_equipment_details: [],
  });
  
  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [filteredSubcategories, setFilteredSubcategories] = useState<Subcategory[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | ''>('');
  const [availableEquipment, setAvailableEquipment] = useState<FullEquipment[]>([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | ''>('');
  const [recommendedEquipmentQuantity, setRecommendedEquipmentQuantity] = useState<number>(1);
  const [currentRecommendedItems, setCurrentRecommendedItems] = useState<ClientToEquipmentComplementaryItem[]>([]);
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);
  const [selectedConsumableId, setSelectedConsumableId] = useState<number | ''>('');
  const [recommendedConsumableQuantity, setRecommendedConsumableQuantity] = useState<number>(1);
  const [currentRecommendedConsumables, setCurrentRecommendedConsumables] = useState<ClientToConsumableComplementaryItem[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loadingDeps, setLoadingDeps] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  
  // תיקון: העברתי את ה-useMemo לראש הקומפוננטה
  const groupedInventory = useMemo(() => groupEquipment(allEquipment), [allEquipment]);
  
  const currentRecommendedItemsForDisplay = useMemo(() => {
    return currentRecommendedItems.map(item => {
        const fullItem = allEquipment.find(eq => eq.id === item.equipment);
        if (!fullItem || fullItem.id === undefined) return null;
        return { ...item, name: fullItem.name, id: fullItem.id };
    }).filter(Boolean);
  }, [currentRecommendedItems, allEquipment]);


  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const [equipmentRes, categoriesRes, subcategoriesRes, consumablesRes] = await Promise.all([
          axios.get(buildApiUrl('inventory/equipment/')),
          axios.get(buildApiUrl('settings/categories/')),
          axios.get(buildApiUrl('settings/subcategories/')),
          axios.get<Consumable[]>(buildApiUrl('settings/consumables/')),
        ]);
        setAllEquipment(equipmentRes.data);
        setCategories(categoriesRes.data);
        setSubcategories(subcategoriesRes.data);
        setAllConsumables(consumablesRes.data);
      } catch (err) {
        setError("לא ניתן לטעון נתונים נדרשים לטופס.");
      } finally {
        setLoadingDeps(false);
      }
    };
    fetchDependencies();
  }, []);

  useEffect(() => {
    if (clientToEdit) {
      setFormData(clientToEdit);
      setCurrentRecommendedItems(clientToEdit.recommended_equipment_details || []);
      setCurrentRecommendedConsumables(clientToEdit.recommended_consumable_details || []);
    } else {
      setFormData({
        client_name: '', contact_person: null, phone: null, email: null,
        address: null, notes: null, recommended_equipment_details: [],
      });
      setCurrentRecommendedItems([]);
      setCurrentRecommendedConsumables([]);
      setSelectedConsumableId('');
    }
  }, [clientToEdit]);

  useEffect(() => {
    if (selectedCategory) {
      setFilteredSubcategories(subcategories.filter(sub => sub.category === Number(selectedCategory)));
      setSelectedSubcategory('');
    } else {
      setFilteredSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory, subcategories]);

  useEffect(() => {
    if (selectedSubcategory) {
      const filtered = allEquipment.filter(eq => 
        eq.subcategory === Number(selectedSubcategory)
      );
      setAvailableEquipment(filtered);
      setSelectedEquipmentId('');
    } else {
      setAvailableEquipment([]);
      setSelectedEquipmentId('');
    }
  }, [selectedSubcategory, allEquipment]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value === '' ? null : value });
  };

  const handleAddRecommendedItem = () => {
    if (selectedEquipmentId && recommendedEquipmentQuantity > 0) {
      const itemToAdd = allEquipment.find(eq => eq.id === Number(selectedEquipmentId));
      if (itemToAdd && itemToAdd.id !== undefined) {
        const existingItemIndex = currentRecommendedItems.findIndex(item => item.equipment === itemToAdd.id);
        if (existingItemIndex > -1) {
          const updatedItems = [...currentRecommendedItems];
          updatedItems[existingItemIndex].quantity += recommendedEquipmentQuantity;
          setCurrentRecommendedItems(updatedItems);
        } else {
          setCurrentRecommendedItems([
            ...currentRecommendedItems,
            {
              equipment: itemToAdd.id,
              equipment_name: itemToAdd.name,
              quantity: recommendedEquipmentQuantity,
            },
          ]);
        }
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSelectedEquipmentId('');
        setRecommendedEquipmentQuantity(1);
        setError(null);
      }
    } else {
      setError("אנא בחר/י פריט והזן/י כמות חוקית.");
    }
  };

  const handleRemoveRecommendedItem = (equipmentId: number) => {
    setCurrentRecommendedItems(currentRecommendedItems.filter(item => item.equipment !== equipmentId));
  };

  const handleAddRecommendedConsumable = () => {
    if (selectedConsumableId && recommendedConsumableQuantity > 0) {
      const c = allConsumables.find((x) => x.id === Number(selectedConsumableId));
      if (c && c.id !== undefined) {
        const idx = currentRecommendedConsumables.findIndex((x) => x.consumable === c.id);
        if (idx > -1) {
          const next = [...currentRecommendedConsumables];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + recommendedConsumableQuantity };
          setCurrentRecommendedConsumables(next);
        } else {
          setCurrentRecommendedConsumables([
            ...currentRecommendedConsumables,
            { consumable: c.id, consumable_name: c.name, quantity: recommendedConsumableQuantity },
          ]);
        }
        setSelectedConsumableId('');
        setRecommendedConsumableQuantity(1);
        setError(null);
      }
    } else {
      setError('אנא בחר/י מתכלה והזן/י כמות חוקית.');
    }
  };

  const handleRemoveRecommendedConsumable = (consumableId: number) => {
    setCurrentRecommendedConsumables(currentRecommendedConsumables.filter((x) => x.consumable !== consumableId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!formData.client_name) {
      setError("שם הלקוח הוא שדה חובה.");
      setSubmitLoading(false);
      return;
    }
    
    const dataToSend: any = { 
      ...formData,
      recommended_equipment_data: currentRecommendedItems.map(item => ({
        equipment: item.equipment,
        quantity: item.quantity,
      })),
      recommended_consumable_data: currentRecommendedConsumables.map((item) => ({
        consumable: item.consumable,
        quantity: item.quantity,
      })),
    };
    
    // מחיקת שדות שלא קיימים במודל כדי למנוע שגיאות
    delete dataToSend.recommended_equipment_details;
    delete dataToSend.recommended_consumable_details;
    
    try {
      if (clientToEdit?.id) {
        await axios.put(buildApiUrl(`settings/clients/${clientToEdit.id}/`), dataToSend);
      } else {
        await axios.post(buildApiUrl('settings/clients/'), dataToSend);
      }
      onSuccess();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response && err.response.data) {
        let errorMessages = '';
        for (const key in err.response.data) {
          if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
            const errorValue = err.response.data[key];
            if (Array.isArray(errorValue)) {
              errorMessages += `${key}: ${errorValue.join(', ')}\n`;
            } else {
              errorMessages += `${key}: ${errorValue}\n`;
            }
          }
        }
        setError(`שגיאה בשמירת הלקוח: \n${errorMessages || 'שגיאה לא ידועה.'}`);
      } else {
        setError("שגיאה בשמירת הלקוח. אנא נסה שוב.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loadingDeps) {
      return <Spinner animation="border" />;
  }

  return (
    <Form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-light">
      <h3 className="mb-4">{clientToEdit ? 'עריכת לקוח' : 'הוספת לקוח חדש'}</h3>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Row className="mb-3">
        <Form.Group as={Col} controlId="formClientName">
          <Form.Label>שם לקוח</Form.Label>
          <Form.Control type="text" name="client_name" value={formData.client_name} onChange={handleChange} required />
        </Form.Group>
        <Form.Group as={Col} controlId="formContactPerson">
          <Form.Label>איש קשר</Form.Label>
          <Form.Control type="text" name="contact_person" value={formData.contact_person || ''} onChange={handleChange} />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col} controlId="formPhone">
          <Form.Label>טלפון</Form.Label>
          <Form.Control type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} />
        </Form.Group>
        <Form.Group as={Col} controlId="formEmail">
          <Form.Label>אימייל</Form.Label>
          <Form.Control type="email" name="email" value={formData.email || ''} onChange={handleChange} />
        </Form.Group>
      </Row>
      <Form.Group className="mb-3" controlId="formAddress">
        <Form.Label>כתובת</Form.Label>
          <Form.Control as="textarea" rows={2} name="address" value={formData.address || ''} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formNotes">
        <Form.Label>הערות</Form.Label>
        <Form.Control as="textarea" rows={3} name="notes" value={formData.notes || ''} onChange={handleChange} />
      </Form.Group>

      <Card className="mt-4 mb-4">
        <Card.Header>ניהול ציוד מומלץ ללקוח</Card.Header>
        <Card.Body>
           <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="compCategory">
                <Form.Label>קטגוריה</Form.Label>
                <Form.Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps}
                >
                  <option value="">בחר קטגוריה...</option>
                  {categories.map(cat => (
                    <option key={`comp-cat-${cat.id}`} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compSubcategory">
                <Form.Label>תת-קטגוריה</Form.Label>
                <Form.Select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedCategory}
                >
                  <option value="">בחר תת-קטגוריה...</option>
                  {filteredSubcategories.map(subcat => (
                    <option key={`comp-sub-${subcat.id}`} value={subcat.id}>
                      {subcat.subcategory_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compEquipment">
                <Form.Label>פריט ציוד</Form.Label>
                <Form.Select
                  value={selectedEquipmentId}
                  onChange={(e) => setSelectedEquipmentId(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedSubcategory}
                >
                  <option value="">בחר פריט...</option>
                  {availableEquipment.map(item => (
                    <option key={`comp-equip-${item.id}`} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="compQuantity">
                <Form.Label>כמות נדרשת</Form.Label>
                <Form.Control
                  type="number"
                  value={recommendedEquipmentQuantity}
                  onChange={(e) => setRecommendedEquipmentQuantity(parseInt(e.target.value) || 1)}
                  min="1"
                  disabled={!selectedEquipmentId}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                onClick={handleAddRecommendedItem}
                disabled={!selectedEquipmentId || recommendedEquipmentQuantity <= 0}
                className="w-100"
              >
                הוסף ציוד מומלץ
              </Button>
            </Col>
          </Row>
          {currentRecommendedItemsForDisplay.length > 0 && (
            <div className="mt-4">
              <h6>פריטים מומלצים שנבחרו:</h6>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>שם פריט</th>
                    <th>כמות</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecommendedItemsForDisplay.map(item => {
                    // ודא שהאובייקט קיים
                    if (!item) return null;
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>
                          <Button variant="danger" size="sm" onClick={() => handleRemoveRecommendedItem(item.id)}>
                            הסר
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4 mb-4">
        <Card.Header>ניהול מתכלים מומלצים ללקוח</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group controlId="clientConsumable">
                <Form.Label>מתכלה</Form.Label>
                <Form.Select
                  value={selectedConsumableId === '' ? '' : String(selectedConsumableId)}
                  onChange={(e) => setSelectedConsumableId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  disabled={loadingDeps}
                >
                  <option value="">בחר מתכלה...</option>
                  {[...allConsumables]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="clientConsumableQty">
                <Form.Label>כמות</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={recommendedConsumableQuantity}
                  onChange={(e) => setRecommendedConsumableQuantity(parseInt(e.target.value, 10) || 1)}
                  disabled={!selectedConsumableId}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button
            variant="outline-primary"
            className="mb-3 w-100"
            onClick={handleAddRecommendedConsumable}
            disabled={!selectedConsumableId || recommendedConsumableQuantity <= 0}
          >
            הוסף מתכלה מומלצת
          </Button>
          {currentRecommendedConsumables.length > 0 && (
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>כמות</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentRecommendedConsumables.map((item) => (
                  <tr key={item.consumable}>
                    <td>{item.consumable_name || allConsumables.find((c) => c.id === item.consumable)?.name}</td>
                    <td>{item.quantity}</td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => handleRemoveRecommendedConsumable(item.consumable!)}>
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        הסר
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end">
        <Button variant="secondary" onClick={onCancel} className="me-2" disabled={submitLoading}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          ביטול
        </Button>
        <Button variant="primary" type="submit" disabled={submitLoading}>
          {submitLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> {clientToEdit ? 'עדכן לקוח' : 'הוסף לקוח'}</>}
        </Button>
      </div>
    </Form>
  );
};

export default ClientForm;
